// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIcon, Button, IconButton, Tooltip } from '@loa/ui-components';
import type { NudgeProviderButtonProps } from '../../types/nudge';

/**
 * NudgeProviderButton — navigates to the full-page Nudge Provider composer
 * ({@code /relationships/:relationshipId/policies/:policyId/nudge}).
 * Previously opened a modal; the page layout unifies with every other
 * advisor compose surface (LOA/CoA invitations, Resend).
 */
export const NudgeProviderButton = ({
  policyId,
  relationshipId,
  hasCompletedLoa,
  variant = 'button',
  disabled = false,
  label,
}: NudgeProviderButtonProps) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (hasCompletedLoa && !disabled) {
      navigate(`/relationships/${relationshipId}/policies/${policyId}/nudge`);
    }
  }, [navigate, relationshipId, policyId, hasCompletedLoa, disabled]);

  const isDisabled = disabled || !hasCompletedLoa;

  let tooltipContent = 'Send a reminder email to the provider about the completed LOA';
  if (!hasCompletedLoa) {
    tooltipContent = 'A completed Letter of Authority is required before nudging the provider';
  } else if (disabled) {
    tooltipContent = 'Nudge is not available';
  }

  if (variant === 'icon') {
    return (
      <Tooltip
        trigger={
          <IconButton
            variant="ghost"
            size="sm"
            onClick={handleClick}
            disabled={isDisabled}
            aria-label="Nudge provider"
            className="nudge-provider-button nudge-provider-button--icon"
            data-testid="nudge-provider-button"
            icon={<AppIcon name="bell" color={isDisabled ? 'grey' : 'blue'} />}
          />
        }
        content={tooltipContent}
        position="top"
        size="small"
      />
    );
  }

  if (variant === 'text') {
    const textLabel = label || 'Nudge';
    const isRefresh = textLabel === 'Refresh';
    return (
      <Tooltip
        trigger={
          <button
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            className={`nudge-provider-text-btn ${isRefresh ? 'nudge-provider-text-btn--refresh' : ''}`}
            data-testid="nudge-provider-button"
            aria-label={textLabel}
          >
            {textLabel}
          </button>
        }
        content={tooltipContent}
        position="top"
        size="small"
      />
    );
  }

  return (
    <Tooltip
      trigger={
        <span>
          <Button
            variant="filled"
            onClick={handleClick}
            disabled={isDisabled}
            aria-label="Nudge provider"
            className="nudge-provider-button nudge-provider-button--full"
            data-testid="nudge-provider-button"
            iconLeft={<AppIcon name="bell" />}
          >
            Nudge Provider
          </Button>
        </span>
      }
      content={tooltipContent}
      position="top"
      size="small"
    />
  );
};

export default NudgeProviderButton;
